<template>
  <div class="overflow-hidden card h-100">
    <div class="pt-3 pb-0 pe-0 card-body">
      <div class="row">
        <div class="col-sm-6">
          <div class="d-flex">
            <div class="ms-3">
              <div class="mb-0 text-sm text-uppercase text-primary font-weight-bold">
                {{ dateRange }}
              </div>
              <h5 class="mb-0 font-weight-bolder mt-1">{{ title }} {{this.lastSynInfo}} </h5>
            </div>
          </div>
        </div>
        <div class="col-sm-6 d-flex justify-content-end mt-3">
          <div class="btn-group me-4" role="group" data-toggle="buttons">
            <label
              v-for="(option, index) in dayOptions"
              :id="index"
              :key="option.name"
              class="btn btn-outline-white"
              :class="{ active: activeIndex === index }"
            >
              <input
                type="radio"
                name="options"
                class="btn-check"
                autocomplete="off"
                :checked="activeIndex === index"
                @click="initChart(index)"
              />
              <span class="d-none d-sm-block p-1">{{ option.name }}</span>
            </label>
          </div>
        </div>

        <div class="col-12">
          <user-optimal-strain-chart v-if="chartData" 
            :labels="chartData.labels"
            :strain-data="chartData.strainData"
            :calories-data="chartData.caloriesData"
            :optimal-min-data="chartData.optimalMinData"
            :optimal-max-data="chartData.optimalMaxData"
            :id="'user-optimal-strain-chart'"
             height="400" class="mt-4"/>  
        </div>
        <div class="row bg-dark py-4">
          <div class="col-12">
            <div class="d-flex justify-content-between">
              <div class="ms-3">
                <h5 id="workoutsTable" class="mb-0 font-weight-bolder mt-1">
                  Workouts
                </h5>
                <div class="text-sm text-light">
                  Higher-intensity workouts increase strain
                </div>
              </div>
            </div>
            <div class="d-flex flex-wrap justify-content-center mt-4">
              <workout-types
                :data-workout-types="dataWorkoutTypes"
                :selected-workout-type-id="selectedWorkoutType"
                @selected-workout-type-id="
                  (selectedValue) => (selectedWorkoutType = selectedValue)
                "
              />
            </div>
          </div>
          <div class="col-12 m-0">
            <div class="table-responsive">
              <el-table
                ref="dataTable"
                :data="userWorkouts.rows"
                style="width: 100%"
                @sort-change="sortColumn"
                @row-click="handleWorkoutRowClick"
              >
                <el-table-column
                  v-for="column in getColumnHeaders(selectedWorkoutType)"
                  :key="column.prop"
                  :prop="column.prop"
                  :label="column.label"
                  :formatter="column.formatter"
                  :min-width="column.minWidth"
                  :width="column.width"
                  :class-name="column.class"
                  sortable="custom"
                  label-class-name="text-capitalize"
                >
                  <template v-if="column.prop == 'strain'" #default="scope">
                    <argon-progress
                      color="success"
                      :percentage="(scope.row.strain / 21) * 100"
                      :left-text="scope.row.strain.toFixed(1)"
                    />
                  </template>
                </el-table-column>
              </el-table>
            </div>
            <div class="card-footer paginator">
            <div class="d-flex justify-content-between align-items-center">
              <span class="ms-3 text-muted">{{ userWorkouts.count }} record(s)</span>
              <app-pagination :records="userWorkouts.count" :page-size="pageSize"
                :page="page"
                @update-page-selection="
                  (selectedValue) => updatePageSelection(selectedValue)
                "
              />
            </div>
          </div>
          </div>
        </div>
        <div class="col-12">
          <user-stress-spo-chart
            v-if="stressAndSpoData"
            id="user-stress-spo-chart"
            height="300"
            :chart-data="stressAndSpoData"
            :date-range="dateRange"
          />
        </div>
        <div class="col-12">
          <user-steps-chart
            v-if="stressAndSpoData"
            id="user-steps-chart"
            height="300"
            :chart-data="stressAndSpoData"
            :date-range="dateRange"
          />
        </div>
      </div>
    </div>
  </div>


    <!-- workout details Modal -->
    <modal
      id="workoutDetailsModal"
      v-model:show="workoutDetailsModal"
      class="modal fade"
      footer-classes="justify-content-end align-items-center"
      :show-close="true"
      :modalContentClasses="'workout-intense-model'"
    >
      <template #header>
        <div class="header-container">
          <div class="text-xl text-capitalize">{{ this.selectedWorkoutName }}</div>
        </div>
      </template>

      <div class="p-2">
          <WorkoutSummary  v-if="this.selectedWorkoutRow" :user="user"
              :workout-data="this.selectedWorkoutRow"></WorkoutSummary>
      </div>
      <template #footer>
        <button type="button" class="btn btn-primary"
          @click="closeModal">DONE</button>
      </template>
    </modal>
</template>

<script>
  const dayjs = require("dayjs");
  import DateUtils from "@/helpers/dateUtils";
  import Constants from "@/helpers/constants.js";
  import ChartUtils from "@/helpers/chartUtils";
  import Utils from "@/helpers/utils";
  import Modal from "@/shared/Modal.vue";
  import ArgonProgress from "@/shared/Controls/ArgonProgress.vue";
  import WorkoutTypes from "../../components/WorkoutTypes.vue";
  import UserStressSpoChart from "../components/insights/UserStressSpoChart.vue";
  import UserStepsChart from "../components/insights/UserStepsChart.vue";
  import WorkoutSummary from "./insights/workout-details/WorkoutSummary.vue";
  import UserOptimalStrainChart from "../components/insights/UserOptimalStrainChart.vue";

  export default {
    name: "UserStrain",
    components: {
      ArgonProgress,
      WorkoutTypes,
      UserStressSpoChart,
      UserStepsChart,
      Modal,
      WorkoutSummary,
      UserOptimalStrainChart,
    },
    props: {
      title: {
        type: String,
        default: "Strain",
      },
      udid: {
        type: String,
        required: true,
      },
      selectedDate: {
        type: String,
        required: true,
      },
      user: {
        type: Object,
        required: true,
      }
    },
    data() {
      return {
        activeIndex: 1,
        options: [30, 14, 7],
        page: Constants.DEFAULT_PAGE,
        pageSize: Constants.DEFAULT_PAGE_SIZE,
        sort: "workout_record_start_time",
        order: "desc",
        userStrainData: [],
        dataWorkoutTypes: [],
        userWorkouts: [],
        workoutTypes: Constants.WORKOUT_TYPES,
        selectedWorkoutType: -1,
        lastSyncDate: '',
        lastSynInfo: '',
        stressAndSpoData: null,
        selectedDateRange: null,
        workoutDetailsModal: false,
        selectedWorkoutRow: null,
        selectedWorkoutName: 'Indoor',
        chartData: null
      };
    },
    computed: {
      dayOptions() {
        let values = [];
        this.options.forEach((opt) => {
          values.push({ name: `${opt} days` });
        });
        return values;
      },
      dateRange() {
        let maxDate = dayjs(this.selectedDate);
        let daysCount = this.options[this.activeIndex];

        return `Last ${daysCount} days : ${maxDate
          .subtract(daysCount - 1, "days")
          .format(Constants.CHART_DATE_FORMAT)} to ${maxDate.format(
          Constants.CHART_DATE_FORMAT
        )}`;

        // return this.selectedDateRange;
      },
    },
    watch: {
      udid() {
        this.initChart(this.activeIndex);
      },
      selectedWorkoutType(newValue) {
        this.page = Constants.DEFAULT_PAGE;
        this.getUserWorkouts(newValue);
      },
      selectedDate() {
        this.getDataForPeriod();
        this.getUserStressAndSpoData(this.udid);
      },
    },
    mounted() {
      if (this.udid) {
        this.initChart(this.activeIndex);
      }
    },
    methods: {
      sortColumn({ prop, order }) {
        this.sort = prop;
        this.order = order == "ascending" ? "asc" : "desc";
        this.getUserWorkouts(this.selectedWorkoutType);
      },
      initChart(index) {
        this.activeIndex = index;
        this.getDataForPeriod();
        this.getUserStressAndSpoData(this.udid);
      },
      getColumnHeaders(workoutType) {
        let columnHeaders = ChartUtils.getColumnHeaders(workoutType);
        // remove name column, since this is for ONE user
        columnHeaders = columnHeaders.filter((c) => c.prop != "name");
        // keep strain column width fixed
        columnHeaders.map((c) => {
          if (c.prop != "strain") delete c.width;
          return c;
        });
        return columnHeaders;
      },
      getDataForPeriod() {
        const daysCount = this.options[this.activeIndex];

        this.$store
          .dispatch("data/userStrainData", {
            udid: this.udid,
            dt_from: dayjs(this.selectedDate)
              .startOf("day")
              .subtract(daysCount - 1, "day")
              .format(Constants.BUCKET_FORMAT),
            dt_to: dayjs(this.selectedDate)
              .startOf("day")
              .format(Constants.BUCKET_FORMAT),
          })
          .then((data) => {
            this.userStrainData = data.data;
            this.lastSyncDate = dayjs(data.lastSyncDate).format(Constants.TABLE_DOB_FORMAT);
            if(dayjs(this.selectedDate).isAfter(dayjs(this.lastSyncDate))) {
              this.lastSynInfo = '- (Last Sync Date: '+this.lastSyncDate+')';
            } else {
              this.lastSynInfo = '';
            }
            let chartDays = [];
            let strainValues = [];
            let calorieValues = [];
            let optimalMin=[]
            let optimalMax=[]

            this.userStrainData.map((record) => {
              strainValues.push(record.strain);
              calorieValues.push(Math.floor(record.calories / 1000));
              if(record.os_range && record.os_range.length == 2) {
                optimalMin.push(record.os_range[0]);
                optimalMax.push(record.os_range[1]);
              } else {
                optimalMin.push(0);
                optimalMax.push(0);
              }

              chartDays.push([
                dayjs(record.bucket).format(Constants.CHART_DATE_FORMAT),
                dayjs(record.bucket).format(
                  Constants.CHART_DATE_DAY_OF_WEEK_FORMAT
                ),
              ]);
            });

            this.chartData = {
              labels: chartDays,
              strainData: strainValues,
              caloriesData: calorieValues,
              optimalMinData: optimalMin,
              optimalMaxData: optimalMax
            };

            // fetch workout types for selected period
            this.getWorkoutTypes();
          });
      },
      getWorkoutTypes() {
        const daysCount = this.options[this.activeIndex];

        this.$store
          .dispatch("data/userWorkoutTypes", {
            udid: this.udid,
            dt_from: dayjs(this.selectedDate)
              .startOf("day")
              .subtract(daysCount - 1, "day")
              .format(Constants.BUCKET_FORMAT),
            dt_to: dayjs(this.selectedDate)
              .startOf("day")
              .format(Constants.BUCKET_FORMAT),
          })
          .then((data) => {
            this.dataWorkoutTypes = data.data;

            // Auto select first workout type
            this.selectedWorkoutType =
              this.dataWorkoutTypes.length > 0
                ? this.dataWorkoutTypes[0].workout_type_record.workout_type_id
                : -1;

            this.page = Constants.DEFAULT_PAGE;
            this.getUserWorkouts(this.selectedWorkoutType);
          });
      },
      getUserWorkouts(workoutType) {
        const daysCount = this.options[this.activeIndex];

        this.$store
          .dispatch("data/userWorkouts", {
            page: this.page,
            page_size: this.pageSize,
            sort: this.sort,
            order: this.order,
            udid: this.udid,
            workout_type: workoutType || -1,
            dt_from: dayjs(this.selectedDate)
              .startOf("day")
              .subtract(daysCount - 1, "day")
              .format(Constants.BUCKET_FORMAT),
            dt_to: dayjs(this.selectedDate)
              .startOf("day")
              .format(Constants.BUCKET_FORMAT),
          })
          .then((data) => {
            this.userWorkouts = data.data;
          });
      },
      updatePageSelection(selectedPage) {
        this.page = selectedPage;
        this.getUserWorkouts(this.selectedWorkoutType);
      },
      getUserStressAndSpoData(udid) {
        const daysCount = this.options[this.activeIndex];

        this.$store.dispatch("data/userStressAndSpoData", {
            udid: udid,
            sort: Constants.STRESS_SORT_COLUMN,
            order: Constants.SORT_ASC,
            dt_from: dayjs(this.selectedDate)
              .startOf("day")
              .subtract(daysCount-1, "day")
              .format(Constants.BUCKET_FORMAT),
            dt_to: dayjs(this.selectedDate)
              .startOf("day")
              .add(1, "day")
              .format(Constants.BUCKET_FORMAT),
          })
          .then((response) => {
            this.stressAndSpoData = null;
            this.transformStressAndSpoData(response.data);
          });
      },
      transformStressAndSpoData(stressAndSpoData){
        let stressScores = [], spoScores=[], steps=[], chartLabels=[];
          if(stressAndSpoData && stressAndSpoData.spo2.length > 0 && stressAndSpoData.stress.length > 0) {
            let spoRawData = stressAndSpoData.spo2.length == stressAndSpoData.stress.length 
              ? stressAndSpoData.spo2 : stressAndSpoData.spo2.slice(1,stressAndSpoData.spo2.length);
            let stressRawData = stressAndSpoData.stress;
            for(let i=0;i<stressRawData.length;i++) {
              stressScores.push(parseInt(stressRawData[i].stress_score ? stressRawData[i].stress_score : 0));
              if(spoRawData[i]) {
                spoScores.push(parseInt(spoRawData[i].spo2 ? spoRawData[i].spo2 : 0));
                steps.push(parseInt(spoRawData[i].steps ? spoRawData[i].steps : 0));
              } else {
                steps.push(0);
                spoScores.push(0);
              }

              let scoreDate = dayjs(stressRawData[i].stress_date);
              let dateMMMDD = DateUtils.formatDateToProvidedDate(stressRawData[i].stress_date, Constants.PERIOD_FORMAT);
              let dayOfWeek = DateUtils.getDayFromDayJsnumber(scoreDate.get('d'));
              chartLabels.push([dateMMMDD,dayOfWeek]);
            }
          }

          this.stressAndSpoData = {
            stressScores: stressScores.reverse(),
            spoScores: spoScores.reverse(),
            steps: steps.reverse(),
            chartLabels: chartLabels.reverse()
          }
      },
      handleWorkoutRowClick(row) {
        this.selectedWorkoutRow = null;
        this.workoutDetailsModal = !this.workoutDetailsModal;
        this.selectedWorkoutRow = row;
        this.selectedWorkoutName = Utils.getWorkoutDescriptionFromID(row.workout_type, this.dataWorkoutTypes);
      },
      closeModal() {
        this.selectedWorkoutRow = null;
        this.workoutDetailsModal = false;
      },
    },
  };
</script>
<style lang="scss" scoped>
  .btn-outline-white {
    background: none;
  }
  label {
    &.active {
      color: $g-gray !important;
    }
  }
  .card .form-select {
    background-color: $color-background-info-pane;
  }
  div .bg-dark h5 {
    color: $lighter !important;
  }
  .header-container {
     position: relative;
    .header-text {
      position: absolute;
      left: 1rem;
      top: 1rem;
    }
 }
</style>
